import { NavController } from '@ionic/angular';
import { UserDeviceService } from './../_core/user-device/user-device.service';
import { NavPage } from './../../models/_core/nav-page';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { User } from 'src/app/models/user';
import { environment } from 'src/environments/environment';

/**
 * ID: bh-navigation
 * Name: BH Navigation Service
 * Description: Service used for managing navigation, main menus, and account options
 * Version: 1
 *
 * ==============================
 * Change Log
 * ==============================
 * 2022-15-25 - MW - v1: Initial development
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  env = environment;

  // Define your pages for this app here
  homeNavPage: NavPage = {
    name: 'Home',
    navPath: '/tabs/patient-landing/BMCLD',
    tabPath: 'home',
    tabId: 'home',
    ionIcon: 'home',
    roles: ['USER', 'ADMIN', 'SYS_ADMIN'],
    isAccessible: true,
    isActive: true
  };

  navPages: NavPage[] = [
    {
      name: 'Events',
      navPath: '/tabs/events',
      tabPath: 'events',
      tabId: 'events',
      ionIcon: 'calendar',
      roles: ['USER', 'ADMIN', 'SYS_ADMIN'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Engage With Us',
      navPath: '/tabs/engage-with-us',
      tabPath: 'engage-with-us',
      tabId: 'engage-with-us',
      ionIcon: 'chatbubbles',
      roles: ['USER', 'ADMIN', 'SYS_ADMIN'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Education',
      navPath: '/tabs/education',
      tabPath: 'education',
      tabId: 'education',
      ionIcon: 'book',
      roles: ['USER', 'ADMIN', 'SYS_ADMIN'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Help Center',
      navPath: '/tabs/help-center',
      tabPath: 'help-center',
      tabId: 'help-center',
      ionIcon: 'help-circle',
      roles: ['USER', 'ADMIN', 'SYS_ADMIN'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Manage',
      navPath: '/tabs/admin-landing',
      tabPath: 'admin-landing',
      tabId: 'admin-landing',
      ionIcon: 'settings',
      roles: ['EDITOR', 'ADMIN', 'SYS_ADMIN'],
      isAccessible: true,
      isActive: true
    }
  ];

  navPagesSubject: BehaviorSubject<NavPage[]> = new BehaviorSubject([]);
  showNotifications = false;
  subscriptions: Subscription[] = [];
  accessiblePages = 0;
  navigationLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private navCtrl: NavController,
    private deviceService: UserDeviceService,
  ) {
    if (this.homeNavPage.isActive) {
      this.navPages.unshift(this.homeNavPage);
    }
    this.navPagesSubject.next(this.navPages);
   }

  checkPrivileges(authUser: User) {
    this.navPages.forEach(page => {
      if (page.roles && page.roles.length > 0) {
        if (authUser) {
          // User logged in, check roles
          const matches = page.roles.filter(r =>
            (r && authUser && authUser.role && r.toLowerCase() === authUser.role.toLowerCase())
          );
          page.isAccessible = (matches.length > 0);
        } else {
          // User not logged in
          page.isAccessible = false;
        }
      } else {
        // Page unprotected
        page.isAccessible = true;
      }
    });

    this.accessiblePages = this.navPages.filter(p => p.isAccessible).length;
    this.navPagesSubject.next(this.navPages);
  }

  navigateHome(): Promise<boolean> {
    const homePage = this.navPages.length > 0 ? this.navPages[0].navPath : '/';
    return this.navigateBack(homePage);
  }

  navigateForward(pathUrl, navOptions = undefined): Promise<boolean> {
    const userDevice = this.deviceService.getUserDevice();
    return this.navCtrl.navigateForward(pathUrl, navOptions);
  }

  navigateBack(pathUrl, navOptions = undefined): Promise<boolean> {
    const userDevice = this.deviceService.getUserDevice();
    return this.navCtrl.navigateBack(pathUrl, navOptions);
  }

  navigateRoot(pathUrl, navOptions = undefined): Promise<boolean> {
    return this.navCtrl.navigateRoot(pathUrl, navOptions);
  }

}
